import { useEffect, useRef, useState } from 'react';
import { devLog } from '../utils/logger';

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/^http/, 'ws');

export function useWebSocket(onMessage) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const onMessageRef = useRef(onMessage);

  // Keep callback ref current without re-triggering connect
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

  useEffect(() => {
    let closedByHook = false;

    const connect = () => {
      if (closedByHook || (wsRef.current && wsRef.current.readyState === WebSocket.OPEN)) return;

      try {
        const ws = new WebSocket(`${WS_BASE}/api/ws`);
        wsRef.current = ws;

        ws.onopen = () => {
          setConnected(true);
          if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
        };

        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            onMessageRef.current(data);
          } catch (err) {
            if (process.env.NODE_ENV === 'development') devLog.error('WebSocket message parse error:', err);
          }
        };

        ws.onclose = () => {
          setConnected(false);
          if (!closedByHook) reconnectRef.current = setTimeout(connect, 3000);
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch (err) {
        if (process.env.NODE_ENV === 'development') devLog.error('WebSocket connection error:', err);
        if (!closedByHook) reconnectRef.current = setTimeout(connect, 3000);
      }
    };

    connect();
    return () => {
      closedByHook = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  return connected;
}
